import React, { useState, useEffect } from "react";
import patientService from "../../../Services/patientService";
import toast from "react-hot-toast";
import { useNavigate, useParams } from "react-router-dom";
import doctorService from "../../../Services/doctorService";
import medicineService from "../../../Services/medicineService";

const SectionLabel = ({ children }) => (
  <p className="text-[11px] uppercase tracking-widest font-[500] text-gray-400 mb-3 mt-6">
    {children}
  </p>
);

const InputField = ({ label, prefix, ...props }) => (
  <div className="flex flex-col gap-1">
    <label className="text-[12px] font-[500] text-gray-500">{label}</label>
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[12px] text-gray-400">
          {prefix}
        </span>
      )}
      <input
        {...props}
        className={`w-full ${prefix ? "pl-8" : "px-3"} pr-3 py-2 text-[13px] text-gray-800 bg-white border border-gray-200 rounded-lg outline-none hover:border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100`}
      />
    </div>
  </div>
);

const AddEditMedicalStore = () => {
  const [patientData, setPatientData] = useState({
    medicine_name: "",
    quantity_in_stock: 0,
    p_price: "",
    price: "",
    expiry_date: "",
    supplier_name: "",
    discount: "",
  });

  const [doctorData, setDoctorData] = useState([]);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const getDoctors = async () => {
      try {
        const response = await doctorService.fetchAllDoctors();
        setDoctorData(response.doctors);
      } catch (error) {
        toast.error("Error fetching Doctors");
      }
    };
    getDoctors();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "discount" && ((value <= 99 && value >= 0) || value === "")) {
      setPatientData({ ...patientData, [name]: value });
    } else if (name !== "discount") {
      setPatientData({ ...patientData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        await medicineService.update(id, patientData);
        toast.success("Medicine updated successfully!");
      } else {
        await medicineService.create(patientData);
        toast.success("Medicine added successfully!");
      }
      navigate("/account/medicinestock");
    } catch (error) {
      toast.error("Error saving patient.");
    }
  };

  return (
    <div className="bg-gray-50 flex items-start justify-center py-6 sm:py-10 px-3 sm:px-4">
      <div className="w-full max-w-[1400px]">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">

          <div className="bg-[#2F6DB4] px-5 sm:px-8 py-4 sm:py-5">
            <h1 className="text-white text-[16px] sm:text-[18px] font-[500] tracking-tight">
              {id ? "Update Medicine" : "Add Medicine"}
            </h1>
            <p className="text-blue-200 text-[11px] sm:text-[12px] mt-0.5">
              Fill in all fields to register the medicine stock record
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="px-4 sm:px-8 py-5 sm:py-6">

              <SectionLabel>Basic Information</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Medicine Name"
                  type="text"
                  name="medicine_name"
                  placeholder="e.g. Paracetamol"
                  value={patientData.medicine_name}
                  onChange={handleChange}
                />
                <InputField
                  label="Purchase Date"
                  type="date"
                  name="expiry_date"
                  value={patientData.expiry_date}
                  onChange={handleChange}
                />
              </div>

              <SectionLabel>Pricing</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <InputField
                  label="Purchase Price"
                  type="number"
                  name="p_price"
                  placeholder="0"
                  prefix="Rs."
                  value={patientData.p_price}
                  onChange={handleChange}
                />
                <InputField
                  label="Sale Price"
                  type="number"
                  name="price"
                  placeholder="0"
                  prefix="Rs."
                  value={patientData.price}
                  onChange={handleChange}
                />
                <InputField
                  label="Discount in Percent"
                  type="number"
                  name="discount"
                  placeholder="0"
                  value={patientData.discount}
                  onChange={handleChange}
                />
              </div>

              <SectionLabel>Supplier Details</SectionLabel>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <InputField
                  label="Supplier Name"
                  type="text"
                  name="supplier_name"
                  placeholder="e.g. MedCo Supplies"
                  value={patientData.supplier_name}
                  onChange={handleChange}
                />
              </div>

              <div className="border-t border-gray-100 mt-8 pt-5 flex flex-col-reverse sm:flex-row items-center sm:justify-between gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/account/medicinestock")}
                  className="w-full sm:w-auto text-center text-[13px] text-gray-400 hover:text-gray-600 transition-colors font-[500] py-2 sm:py-0"
                >
                  ← Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto relative overflow-hidden bg-[#2F6DB4] text-white text-[13px] font-[500] px-6 py-2.5 rounded-lg shadow-md hover:shadow-gray-200 hover:scale-[1.03] active:scale-[0.97] transition-all duration-300 before:absolute before:inset-0 before:-translate-x-full hover:before:translate-x-full before:bg-white/20 before:transition-transform before:duration-500"
                >
                  {id ? "Update Medicine →" : "Save Medicine →"}
                </button>
              </div>

            </div>
          </form>
        </div>

        <p className="text-center text-[12px] text-gray-400 mt-4">
          All fields marked as required must be filled before saving.
        </p>
      </div>
    </div>
  );
};

export default AddEditMedicalStore;